import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Volume2, Square } from 'lucide-react';
import useTextToSpeech from '@/hooks/useTextToSpeech';
import { useAbielAudio } from '@/components/audio/AbielAudioContext';

interface TTSControlsProps {
  text?: string | null;
  disabled?: boolean;
}

// Lightweight, reusable TTS controls with a single play/stop toggle
const TTSControls: React.FC<TTSControlsProps> = ({ text, disabled }) => {
  const { isPlaying } = useAbielAudio();
  const [isSpeaking, setIsSpeaking] = useState(false);
  const { speak, stop, isLoading } = useTextToSpeech({ keepLastUrl: false });

  const canPlay = useMemo(() => !!text && !disabled && !isLoading, [text, disabled, isLoading]);

  const handlePlay = async () => {
    if (!text || !canPlay) return;
    try {
      const sessionId = `ui-tts-${Date.now()}`;
      await speak(sessionId, text);
      setIsSpeaking(true);
    } catch (e) {
      // swallow, errors are handled inside the hook
      setIsSpeaking(false);
    }
  };

  const handleStop = async () => {
    try {
      await stop();
    } finally {
      setIsSpeaking(false);
    }
  };

  const showingStop = isPlaying || isSpeaking;

  return (
    <>
      {!showingStop ? (
        <Button
          onClick={handlePlay}
          variant="outline"
          size="sm"
          className="flex-shrink-0"
          disabled={!canPlay}
          aria-label="Play response with text-to-speech"
        >
          <Volume2 className="h-4 w-4" />
        </Button>
      ) : (
        <Button
          onClick={handleStop}
          variant="outline"
          size="sm"
          className="flex-shrink-0"
          aria-label="Stop text-to-speech playback"
        >
          <Square className="h-4 w-4" />
        </Button>
      )}
    </>
  );
};

export default TTSControls;
